import {
  Form,
  Heading,
  Text,
  PhoneField,
  TextField,
  SingleSelectField,
  NumberField,
  HStack,
  Box,
  CircularProgress,
  SubmitButton,
} from '@medely/ui-kit';
import React from 'react';
import { Divider } from '@mp/components/divider/Divider';
import { personalValidationSchema } from './personalValidationSchema';
import { usePersonalFormData } from './usePersonalFormData';
import { PersonalFormValuesProps } from './types';
import { usePersonalFormSubmit } from './usePersonalFormSubmit';
import { FixedFooterLayout } from '@mp/components';

const DividerBox = () => (
  <Box width="100%" py={2}>
    <Divider />
  </Box>
);
export const PersonalForm = () => {
  const { defaultValues, loadingData, stateOptions } = usePersonalFormData();
  const { createEmergencyContact, isSubmitting, onSubmitAccount, updateEmergencyContact } =
    usePersonalFormSubmit();
  const fixedFooterLayoutHeight = '92%';
  const hasNewEmergencyContactData = (
    values: PersonalFormValuesProps,
    initialValues: typeof defaultValues,
  ) => {
    const hasNewFirstName = values.emergency_first_name !== initialValues.emergency_first_name;
    const hasNewLastName = values.emergency_last_name !== initialValues.emergency_last_name;
    const hasNewPhone = values.emergency_phone_number !== initialValues.emergency_phone_number;

    return hasNewFirstName || hasNewLastName || hasNewPhone;
  };

  const createOrUpdateEmergencyContact = ({ values }: { values: PersonalFormValuesProps }) => {
    const hasNewEmergencyData = hasNewEmergencyContactData(values, defaultValues);
    if (hasNewEmergencyData && values.emergency_id) {
      updateEmergencyContact(values);
    } else if (hasNewEmergencyData) {
      createEmergencyContact(values);
    }
  };

  const handleFormSubmit = (values: PersonalFormValuesProps) => {
    const hasNewEmergencyData = hasNewEmergencyContactData(values, defaultValues);
    if (hasNewEmergencyData) {
      createOrUpdateEmergencyContact({ values });
    }
    onSubmitAccount(values);
  };

  if (loadingData) {
    return <CircularProgress />;
  }

  return (
    <Form
      handleSubmit={handleFormSubmit}
      schema={personalValidationSchema}
      defaultValues={defaultValues}
    >
      <Heading size="xs">Personal information</Heading>
      <Box py={0.5} />
      <FixedFooterLayout
        height={fixedFooterLayoutHeight}
        footerContent={
          <SubmitButton loading={isSubmitting} disabled={isSubmitting} fullWidth>
            Save
          </SubmitButton>
        }
      >
        <TextField name="nickname" label="" placeholder="Preferred name" />
        <PhoneField name="phone_number" label="" placeholder="Phone number*" />
        <DividerBox />
        <Box py={0.5} />
        <Heading size="xs">Home address</Heading>
        <Box py={0.5} />
        <Text size="m">
          Update your address to get matched to local per-diem shifts and assignments.
        </Text>
        <TextField name="address_first" label="" placeholder="Street Address*" />
        <TextField name="address_second" label="" placeholder="Unit, Suite, Building number" />
        <TextField name="address_city" label="" placeholder="City*" />

        <HStack width="100%">
          <Box width="48%">
            <SingleSelectField
              name="state_id"
              label=""
              options={stateOptions}
              placeholder="State*"
            />
          </Box>
          <Box width="4%" />
          <Box width="48%">
            <NumberField name="address_zip" label="" placeholder="Zip code*" />
          </Box>
        </HStack>
        <DividerBox />
        <Box py={0.5} />
        <Heading size="xs">Emergency contact</Heading>

        <HStack width="100%">
          <Box width="48%">
            <TextField name="emergency_first_name" label="" placeholder="First name*" />
          </Box>
          <Box width="4%" />
          <Box width="48%">
            <TextField name="emergency_last_name" label="" placeholder="Last name*" />
          </Box>
        </HStack>
        <PhoneField name="emergency_phone_number" label="" placeholder="Phone number*" />
        <DividerBox />
      </FixedFooterLayout>
    </Form>
  );
};
